/**
 * CohortDrilldownModal — click-to-modal drill-down for the weekly cohort
 * retention heatmap.
 *
 * A cell (cohortWeek × weekOffset) splits the cohort's members into two
 * disjoint lists that reconcile against the parent cell by construction:
 *   - `active`  — had ≥1 reactive trace in the retention week
 *                 (len(active) == cell.activeUsers)
 *   - `dropped` — joined this cohort but had 0 traces that week
 *                 (len(active) + len(dropped) == cell.cohortSize)
 *
 * Presented as a Radix Tabs pair so an analyst can flip between "who's
 * still with us" and "who we lost" without losing their place. Counts
 * are surfaced in the tab labels so it reads right from the glance.
 *
 * Data source: `useCohortDrilldown(cohortWeek, weekOffset, filters)` —
 * inert while the modal is closed (`cohortWeek`/`weekOffset` are null).
 */

import * as React from 'react';

import { UserNameCell } from '@/components/UserNameCell';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useCohortDrilldown } from '@/hooks/use-engagement';
import { isoWeekLabel, isoWeekRangeTooltip } from '@/lib/iso-week';
import { formatDateShort, formatDateTimeFull } from '@/lib/format-date';
import type { Filters } from '@/lib/filters-schema';
import type { CohortDrilldownUser } from '@/services/api-types';

interface CohortDrilldownModalProps {
  cohortWeek: string | null;
  weekOffset: number | null;
  filters: Filters;
  onClose: () => void;
}

function cohortHeader(cohortWeek: string): string {
  return cohortWeek === 'older'
    ? 'Older cohorts (folded)'
    : `Cohort ${isoWeekLabel(cohortWeek)}`;
}

function cohortSubtitle(cohortWeek: string): string | null {
  if (cohortWeek === 'older') return null;
  return isoWeekRangeTooltip(cohortWeek);
}

/**
 * Dedup array by `userId`. Defensive safety net against backend row
 * multiplication (e.g. an OR-join on `user_role_cache` fanning out one
 * user into N rows). Keeps first occurrence so the SQL-side sort is
 * preserved. First match wins; subsequent duplicates are dropped.
 */
function uniqByUserId<T extends { userId: string }>(arr: T[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const u of arr) {
    if (seen.has(u.userId)) continue;
    seen.add(u.userId);
    out.push(u);
  }
  return out;
}

function formatDaysAgo(iso: string): string | null {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const days = Math.floor((Date.now() - t) / (1000 * 60 * 60 * 24));
  if (days <= 0) return 'today';
  if (days === 1) return '1 day ago';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  return months === 1 ? '1 month ago' : `${months} months ago`;
}

function RmBadge(): React.ReactElement {
  return (
    <span
      data-testid="cohort-drilldown-rm-badge"
      className="inline-flex items-center rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-400"
    >
      RM
    </span>
  );
}

function FirstSeenCell({ iso }: { iso: string }): React.ReactElement {
  const relative = React.useMemo(() => formatDaysAgo(iso), [iso]);
  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <span className="cursor-default tabular-nums text-muted-foreground">
          {formatDateShort(iso)}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" align="start" sideOffset={6}>
        <div className="text-xs">
          {relative && (
            <div className="font-semibold text-foreground">{relative}</div>
          )}
          <div className="text-muted-foreground tabular-nums">
            {formatDateTimeFull(iso)}
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

function UserRow({
  user,
  filters,
  tab,
}: {
  user: CohortDrilldownUser;
  filters: Filters;
  tab: 'active' | 'dropped';
}): React.ReactElement {
  const from = filters.from ?? undefined;
  const to = filters.to ?? undefined;
  return (
    <div
      data-testid="cohort-drilldown-row"
      data-username={user.userId}
      data-tab={tab}
      className="flex items-center gap-3 rounded-md border border-border/60 bg-card px-3 py-2"
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {user.isRm && <RmBadge />}
        <UserNameCell
          username={user.userId}
          realName={user.realName}
          from={from}
          to={to}
          dataTestId="cohort-drilldown-user"
        />
      </div>
      <div className="flex shrink-0 items-center gap-3 text-xs tabular-nums text-muted-foreground">
        <span className="whitespace-nowrap">
          joined <FirstSeenCell iso={user.firstSeenAt} />
        </span>
        {tab === 'active' && (
          <span
            data-testid="cohort-drilldown-requests"
            className="whitespace-nowrap font-semibold text-foreground"
            title="Reactive traces in this retention week"
          >
            {user.requestsInWindow} req
            {user.requestsInWindow === 1 ? '' : 's'}
          </span>
        )}
      </div>
    </div>
  );
}

function UserList({
  users,
  filters,
  tab,
  emptyHint,
}: {
  users: CohortDrilldownUser[];
  filters: Filters;
  tab: 'active' | 'dropped';
  emptyHint: string;
}): React.ReactElement {
  if (users.length === 0) {
    return (
      <div
        data-testid={`cohort-drilldown-empty-${tab}`}
        className="rounded-md border border-border bg-muted/30 p-4 text-center text-sm text-muted-foreground"
      >
        {emptyHint}
      </div>
    );
  }
  return (
    <TooltipProvider delayDuration={200}>
      <div
        data-testid={`cohort-drilldown-list-${tab}`}
        className="flex flex-col gap-1.5"
      >
        {users.map((u) => (
          <UserRow key={u.userId} user={u} filters={filters} tab={tab} />
        ))}
      </div>
    </TooltipProvider>
  );
}

export function CohortDrilldownModal({
  cohortWeek,
  weekOffset,
  filters,
  onClose,
}: CohortDrilldownModalProps): React.ReactElement {
  const open = cohortWeek !== null && weekOffset !== null;

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (!next) onClose();
    },
    [onClose],
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="cohort-drilldown-modal"
        data-cohort-week={cohortWeek ?? ''}
        data-week-offset={weekOffset ?? ''}
        className="max-w-[min(96vw,48rem)] w-[min(96vw,48rem)] max-h-[88vh] overflow-hidden flex flex-col"
      >
        {cohortWeek !== null && weekOffset !== null && (
          <CohortDrilldownBody
            cohortWeek={cohortWeek}
            weekOffset={weekOffset}
            filters={filters}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function CohortDrilldownBody({
  cohortWeek,
  weekOffset,
  filters,
}: {
  cohortWeek: string;
  weekOffset: number;
  filters: Filters;
}): React.ReactElement {
  const query = useCohortDrilldown(cohortWeek, weekOffset, filters);
  const subtitle = cohortSubtitle(cohortWeek);

  // Defensive dedup by userId — belt-and-braces against backend row
  // multiplication (e.g. OR-join fan-out on user_role_cache). The SQL
  // side is the root-cause fix; this guard keeps the UI honest if any
  // future query change regresses.
  const active = uniqByUserId(query.data?.active ?? []);
  const dropped = uniqByUserId(query.data?.dropped ?? []);
  const cohortSize = query.data?.cohortSize ?? active.length + dropped.length;

  return (
    <>
      <DialogHeader>
        <DialogTitle data-testid="cohort-drilldown-title">
          {cohortHeader(cohortWeek)} · week +{weekOffset}
        </DialogTitle>
        <DialogDescription
          data-testid="cohort-drilldown-subtitle"
          className="text-xs text-muted-foreground"
        >
          {subtitle ? `${subtitle} — ` : ''}
          who from this cohort was still active {weekOffset} week
          {weekOffset === 1 ? '' : 's'} after joining, and who had dropped.
        </DialogDescription>
      </DialogHeader>

      {query.isLoading && (
        <div
          data-testid="cohort-drilldown-loading"
          className="space-y-2 p-1"
        >
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-10 rounded-md border border-border bg-muted/40 animate-pulse"
            />
          ))}
        </div>
      )}

      {query.isError && (
        <div
          data-testid="cohort-drilldown-error"
          className="flex items-center justify-between gap-3 rounded-md border border-destructive/40 bg-background p-3 text-sm text-destructive"
        >
          <span>Failed to load the cohort drill-down.</span>
          <button
            type="button"
            onClick={() => void query.refetch()}
            className="underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      )}

      {query.data && !query.isLoading && !query.isError && (
        <div className="min-h-0 flex-1 overflow-hidden flex flex-col gap-3">
          <div
            data-testid="cohort-drilldown-summary"
            className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground"
          >
            <span>
              <span
                data-testid="cohort-drilldown-active-count"
                className="font-semibold text-emerald-700 dark:text-emerald-400 tabular-nums"
              >
                {active.length}
              </span>{' '}
              active
            </span>
            <span>
              <span
                data-testid="cohort-drilldown-dropped-count"
                className="font-semibold text-muted-foreground tabular-nums"
              >
                {dropped.length}
              </span>{' '}
              dropped
            </span>
            <span>·</span>
            <span>
              Cohort size{' '}
              <span className="font-semibold text-foreground tabular-nums">
                {cohortSize}
              </span>
            </span>
            <span>·</span>
            <span data-testid="cohort-drilldown-role-split">
              RM{' '}
              <span className="font-semibold text-foreground tabular-nums">
                {active.filter((u) => u.isRm).length +
                  dropped.filter((u) => u.isRm).length}
              </span>
              {' · '}
              Non-RM{' '}
              <span className="font-semibold text-foreground tabular-nums">
                {active.filter((u) => !u.isRm).length +
                  dropped.filter((u) => !u.isRm).length}
              </span>
            </span>
          </div>

          <Tabs
            defaultValue="active"
            className="flex min-h-0 flex-1 flex-col"
            data-testid="cohort-drilldown-tabs"
          >
            <TabsList className="self-start">
              <TabsTrigger value="active" data-testid="cohort-drilldown-tab-active">
                Active ({active.length})
              </TabsTrigger>
              <TabsTrigger
                value="dropped"
                data-testid="cohort-drilldown-tab-dropped"
              >
                Dropped ({dropped.length})
              </TabsTrigger>
            </TabsList>
            <TabsContent
              value="active"
              className="mt-3 min-h-0 flex-1 overflow-y-auto pr-1"
            >
              <UserList
                users={active}
                filters={filters}
                tab="active"
                emptyHint="No one from this cohort was active that week."
              />
            </TabsContent>
            <TabsContent
              value="dropped"
              className="mt-3 min-h-0 flex-1 overflow-y-auto pr-1"
            >
              <UserList
                users={dropped}
                filters={filters}
                tab="dropped"
                emptyHint="Everyone from this cohort was active that week — no drop-offs."
              />
            </TabsContent>
          </Tabs>
        </div>
      )}
    </>
  );
}
